import SkeletonBlogList from "@/components/BlogSkeleton";
import { structuredData } from "@/data/structuredData";
import useWindowSize from "@/hooks/useWindowSize";
import styles from "@/styles/Resources.module.scss";
import { debounce, decodeHtml } from "@/utils/helpers";
import { fetchAPI } from "@/utils/strapi";
import axios from "axios";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { Col, Container, Form, InputGroup, Row, Tab, Tabs } from "react-bootstrap";

const Layout = dynamic(() => import("@/components/Layout"));
const StructuredData = dynamic(() => import("@/components/StructuredData"));
const ButtonIcon = dynamic(() => import("@/components/ButtonIcon"));
const Contact = dynamic(() => import("@/components/Contact"));
const Pagination = dynamic(() => import("@/components/Pagination"));
// const Resource = dynamic(() => import("@/components/Resource"));
const BlogList = dynamic(() => import("@/components/list/BlogList"));
const Loading = dynamic(() => import("@/components/Loading"));

const Blog = () => {
  const router = useRouter();
  const [width] = useWindowSize();
  const [data, setData] = useState<any[]>([]);
  const [category, setCategory] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(""); // State for search input value

  useEffect(() => {
    fetchCategory();
    fetchData("/posts", 1);
  }, []);

  const sendSearchData = async (data: { SearchTerm: string; Date: string; GotResult: boolean }) => {
    try {
      await axios
        .post(
          `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/search-terms`,
          { data: data },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
            },
          }
        )
    } catch (error) {
      console.error("Error logging search data:", error);
    }
  };
  // const onSearch = async (value: string) => {
  //   setLoading(true);
  //   const currentDate = new Date().toISOString(); // Get the current date in ISO format

  //   // Fetch filtered results from your API
  //   const res = await fetchAPI("/posts", {
  //     populate: "*",
  //     "pagination[pageSize]": 10,
  //     "filters[Title][$containsi]": value, // Case-insensitive partial match
  //     "sort[5]": "Date:desc",
  //   });
  //   const GotResult = res.data?.length > 0; // Check if there are results

  //   setData(res.data); // Update the data with filtered results
  //   setPaginationData(res.meta); // Update pagination if necessary
  //   setLoading(false);
  //   const searchData = {
  //     SearchTerm: value,
  //     Date: currentDate,
  //     GotResult,
  //   };

  //   // Send search data to /api/search-terms in the background
  //   sendSearchData(searchData);
  // };

  const onSearch = (value: string, category: string) => {
    router.push(
      `/resources/searchResults?keyword=${encodeURIComponent(value)}&category=${encodeURIComponent(
        category
      )}`
    );
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchValue.trim()) {
      onSearch(searchValue.trim(), "blogs");
    } else if (e.key === "Enter") {
      e.preventDefault(); // Prevent form submission when the field is empty
    }
  };

  const handleSearchClick = () => {
    if (searchValue.trim()) {
      onSearch(searchValue.trim(), "blogs");
    }
  };

  const fetchCategory = async () => {
    const res = await fetchAPI("/categories", {
      populate: "*",
      "sort[5]": "Date:desc",
      "filters[ResourceType]": {
        $contains: "blog",
      },
    });

    setCategory(res.data);
  };

  const fetchData = async (endpoint: string, page: number) => {
    page > 1 &&
      (document as any).querySelector(".blog").scrollIntoView({
        behavior: "smooth",
      });
    setLoading(true);

    const res = await fetchAPI(endpoint, {
      populate: ["Tags","Author","Banner","BannerThumb"],
      "pagination[page]": page,
      "pagination[pageSize]": 10,
      "sort[5]": "Date:desc",
      fields: [
        "Date",
        "Title",
        "ShortDescription",
        "Slug",
        "BannerImageURL",
      ],
    });
    setData(res.data);
    setPaginationData(res.meta);
    setLoading(false);
  };

  const onChangePage = async (event: any, page: number) => {
    event.preventDefault();

    await fetchData("/posts", page);
  };

  const renderData = (data: any[]) => {
    const bckgrndImg = width <= 767 ? data[0]?.attributes?.BannerThumb?.data?.attributes?.url || data[0]?.attributes?.Banner?.data?.attributes?.formats?.medium?.url : data[0]?.attributes?.Banner?.data?.attributes?.formats?.medium?.url
    return (
      <>
        {data?.length > 0 && (
          <div className="scale-container">
            <div
              className="casestudy-box pointer"
              onClick={(e) => {
                e.preventDefault()
                if (e?.ctrlKey)
                  window.open(`/resources/blog/${data[0].attributes.Slug}`, '_blank')
                else
                  router.push(`/resources/blog/${data[0].attributes.Slug}`)
              }
              }
            >
              <div
                className="bg-scale-img"
                style={{
                  backgroundImage: `url(${bckgrndImg?.replace(
                    "cubet-website-storage-live.s3.us-west-2.amazonaws.com",
                    "cubet-website-storage-live.s3-accelerate.amazonaws.com"
                  ) ||
                    data[0]?.attributes?.BannerImageURL ||
                    "/assets/images/blog-common.jpg"
                    })`,
                }}
              ></div>
              <div className="text-content">
                <div className="d-flex align-items-center justify-content-between w-100">
                  <div>
                    <p className="h3">{decodeHtml(data[0].attributes.Title)}</p>
                    <p className="mb-0">
                      {decodeHtml(data[0]?.attributes?.ShortDescription)
                        .replace(/<\/?[^>]+(>|$)/g, "")
                        .substring(0, 100)}{" "}
                      ...
                    </p>
                  </div>
                  <ButtonIcon
                    link={`/resources/blog/${data[0].attributes.Slug}`}
                    bgClass="blue-link"
                    iconType="white"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {data?.slice(1).map((item: any) => (
          <BlogList data={item} key={item.id} />
        ))}
      </>
    );
  };


  const handleTabSelect = (tab: string | null) => {
    if (tab === "blog") router.push("/resources/blog");
    else if (tab === "case") router.push("/resources/case-studies");
    else if (tab === "whitepapers") router.push("/resources/whitepapers");
    else if (tab === "news") router.push("/resources/news");
    else if (tab === "Voices & Perspectives") router.push("/resources/voices-and-perspectives");
    else router.push("/resources/insights");
  };

  return (
    <Layout
      title="Stay Updated with the Latest in Tech | Blog | Cubet"
      description="Checkout Cubet's blogs and know more about new technologies, information about the real-time use of software, web application technologies and much more!!"
      banner={true}
    >
      <StructuredData data={[structuredData.blog1, structuredData.blog2]} />
      <div className={styles.insights}>
        <div className="header">
          <Container>
            <Row className="align-items-center justify-content-between">
              <Col>
                <h1 className="heading">Insights from our team</h1>
                <p className="normal-text">
                  Expert perspectives to elevate your ideas and strategies.
                </p>
              </Col>
              <div className="blog-search">
                <InputGroup className="mb-3">
                  <InputGroup.Text id="basic-addon1">
                    <img src="/assets/icons/search.svg" alt="Search Icon" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="search"
                    placeholder="Search"
                    autoComplete="none"
                    className="form-control"
                    width={"400"}
                    style={{ boxShadow: "none" }}
                    value={searchValue} // Bind input value to state
                    onChange={(e) => setSearchValue(e.target.value)} // Update state on input change
                    onKeyPress={handleSearchKeyPress} // Handle Enter key press
                  />
                  <button
                    className="search-arrow-btn"
                    onClick={handleSearchClick} // Handle search button click
                    style={{
                      border: "none",
                      cursor: searchValue.trim() === "" ? "default" : "pointer", // Change cursor style when disabled
                      padding: "0 15px",
                    }}
                    disabled={!searchValue.trim()} // Disable button when input is empty
                  >
                    <img
                      src="/assets/icons/search-right-arrow.svg"
                      alt="Search Arrow Icon"
                      style={{ height: "20px", width: "20px", opacity: searchValue.trim() === "" ? 0.7 : 1, }}
                    />
                  </button>
                </InputGroup>
              </div>

            </Row>
          </Container>
        </div>
        <div className="blog">
          <Container>
            <Row>
              <Col>
                <Tabs defaultActiveKey="blog" onSelect={handleTabSelect}>
                  <Tab eventKey="blog" title={<Link href="/resources/blog" passHref>
                    Blogs
                  </Link>}
                  >
                    <Row>
                      <Col
                        lg={3}
                        className="mb-md-5 mb-lg-0 order-2 order-lg-1"
                      >
                        <p className="h6">Blog Topics</p>
                        <ul className="sidebar">
                          {category
                            ?.sort((a: any, b: any) => a.attributes.Title.localeCompare(b.attributes.Title))
                            .map((item: any) => (
                              <li key={item.id}>
                                <Link
                                  prefetch={false}
                                  href={`/resources/blog/category/${item?.attributes?.Slug}`}
                                >
                                  {item?.attributes?.Title}
                                </Link>
                              </li>
                            ))}
                        </ul>
                      </Col>
                      <Col className="order-1 order-lg-2">
                        {loading ? (
                          <SkeletonBlogList count={3} />
                        ) : (
                          <>
                            {renderData(data)}
                            {paginationData && (
                              <div className={data?.length === 1 ? "mt-4" : ""}>
                                <Pagination
                                  data={paginationData.pagination}
                                  onChangePage={onChangePage}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </Col>
                    </Row>
                  </Tab>
                  <Tab eventKey="case" title={<Link href="/resources/case-studies" passHref>
                    Case Studies
                  </Link>}
                  ></Tab>
                  <Tab eventKey="whitepapers" title={
                    <Link href="/resources/whitepapers" passHref>
                      Whitepapers
                    </Link>
                  }
                  ></Tab>
                  <Tab eventKey="insights" title={
                    <Link href="/resources/insights" passHref>
                      Insights
                    </Link>
                  }
                  ></Tab>
                  <Tab eventKey="news" title={
                    <Link href="/resources/news" passHref>
                      News
                    </Link>
                  }
                  ></Tab>
                  <Tab eventKey="Voices & Perspectives" title={
                    <Link href="/resources/voices-and-perspectives" passHref>
                      Voices & Perspectives
                    </Link>
                  }
                  ></Tab>
                </Tabs>
              </Col>
            </Row>
          </Container>
          {/* <Resource /> */}
        </div>
        <Contact type="fixed" title="Contact Us" />
      </div>
    </Layout>
  );
};

export default Blog;
